import { useState, useEffect } from "react";
import { collection, addDoc, getDocs, onSnapshot, serverTimestamp } from "firebase/firestore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES, STAFF, STAFF_COLORS } from "../../config/constants";
import { db } from "../../firebase";
import { staffAvatar, btnP, btnS } from "../../utils/styles";

export default function NewTaskModal({ currentUser, onClose, theme, clients, prefillClient }) {
  const t = THEMES[theme];
  const [form, setForm] = useState({
    title:"", description:"", assignedTo:"",
    clientId: prefillClient?.id||"", clientName: prefillClient?.name||"",
    businessId:"", businessName:""
  });
  const [saving, setSaving] = useState(false);
  const [odLinks, setOdLinks] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = { background:t.inputBg||t.bg, border:"1px solid "+t.border, borderRadius:8, padding:"9px 12px", color:t.text, fontSize:13, outline:"none", fontFamily:"'Outfit',sans-serif", width:"100%", boxSizing:"border-box" };

  // Load OneDrive links
  useEffect(()=>{
    getDocs(collection(db,"onedrive_links")).then(snap=>{
      const map={};
      snap.forEach(d=>{ if(d.data().clientId) map[d.data().clientId]={folderId:d.id,...d.data()}; });
      setOdLinks(map);
    }).catch(()=>{});
  },[]);

  // Load businesses
  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"businesses"), snap=>{
      setBusinesses(snap.docs.map(d=>({id:d.id,...d.data()})));
    });
    return unsub;
  },[]);

  // Build combined searchable list
  const buildOptions = (search) => {
    const s = (search||"").toLowerCase();
    const results = [];
    // Clients
    clients.filter(c=>c.name?.toLowerCase().includes(s)).sort((a,b)=>a.name.localeCompare(b.name)).slice(0,15).forEach(c=>{
      results.push({ type:"client", id:c.id, name:c.name, label:c.name });
    });
    // Businesses
    businesses.filter(b=>b.legalName?.toLowerCase().includes(s) || b.dba?.toLowerCase().includes(s)).sort((a,b)=>(a.legalName||"").localeCompare(b.legalName||"")).slice(0,15).forEach(b=>{
      const owner = clients.find(c=>c.id===b.ownerId);
      results.push({ type:"business", id:b.id, name:b.legalName||b.dba||"Unnamed", ownerId:b.ownerId, ownerName:owner?.name||b.ownerName||"", label:(b.legalName||b.dba||"Unnamed") });
    });
    return results.slice(0,20);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { alert("Title required"); return; }
    if (!form.assignedTo) { alert("Please assign to a staff member"); return; }
    setSaving(true);
    try {
      // SharePoint URL from client
      const resolvedClientId = form.clientId || "";
      const odData = resolvedClientId ? odLinks[resolvedClientId] : null;
      const sharePointUrl = odData?.webUrl || odData?.sharePointUrl || null;

      await addDoc(collection(db,"tasks"), {
        title: form.title.trim(),
        description: form.description.trim(),
        assignedTo: form.assignedTo,
        createdBy: currentUser,
        clientId: resolvedClientId || null,
        clientName: form.clientName || null,
        businessId: form.businessId || null,
        businessName: form.businessName || null,
        oneDriveUrl: sharePointUrl,
        status: "open",
        comments: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      onClose();
    } catch(e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const displayName = form.businessName ? form.businessName + (form.clientName ? " ("+form.clientName+")" : "") : (form.clientName || "");

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:t.surface, borderRadius:16, width:"100%", maxWidth:480, boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid "+t.border, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:17, fontWeight:800, color:t.text }}>New Task</div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", padding:4 }}><Icon name="x" size={18} color={t.textSub}/></button>
        </div>
        <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>
          <div>
            <div style={{ fontSize:11, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Title *</div>
            <input value={form.title} onChange={e=>set("title",e.target.value)} placeholder="e.g. Data entry for John Smith" style={inp}/>
          </div>
          <div>
            <div style={{ fontSize:11, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Description</div>
            <textarea value={form.description} onChange={e=>set("description",e.target.value)}
              placeholder="Add details..." style={{ ...inp, minHeight:80, resize:"vertical" }}/>
          </div>
          <div>
            <div style={{ fontSize:11, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Assign To *</div>
            <div style={{ display:"flex", gap:8 }}>
              {STAFF.map(s=>(
                <button key={s} type="button" onClick={()=>set("assignedTo",s)}
                  style={{ flex:1, padding:"9px 0", borderRadius:8, border:"2px solid "+(form.assignedTo===s?STAFF_COLORS[s]:t.border), background:form.assignedTo===s?STAFF_COLORS[s]+"18":"transparent", color:form.assignedTo===s?STAFF_COLORS[s]:t.textMuted, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"'Outfit',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                  {staffAvatar(s,18)}{s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize:11, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:5 }}>Link to Client or Business (optional)</div>
            {/* Selected display */}
            {displayName && !form._search && form._search!=='' && (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", border:"1px solid "+t.border, borderRadius:8, background:t.bg, marginBottom:4 }}>
                <span style={{ fontSize:11, fontWeight:700, padding:"2px 7px", borderRadius:6, background: form.businessId ? BRAND.green+"18" : BRAND.blue+"18", color: form.businessId ? BRAND.green : BRAND.blue }}>{form.businessId ? "Business" : "Client"}</span>
                <span style={{ fontSize:13, color:t.text, flex:1 }}>{displayName}</span>
                <button onClick={()=>{set("clientId","");set("clientName","");set("businessId","");set("businessName","");set("_search","");}} style={{ background:"none", border:"none", cursor:"pointer", color:t.textMuted, fontSize:14 }}>×</button>
              </div>
            )}
            {/* Search input */}
            <div style={{ position:"relative" }}>
              <input type="text" placeholder="Search client or business..."
                value={form._search!=null ? form._search : ""}
                onChange={e=>set("_search",e.target.value)}
                onFocus={()=>{ if(displayName && form._search==null) set("_search",""); }}
                style={{...inp, display: (displayName && form._search==null) ? "none" : "block"}}/>
              {form._search!=null && form._search!=="" && (
                <div style={{ position:"absolute", zIndex:50, top:"100%", left:0, right:0, background:t.surface, border:"1px solid "+t.border, borderRadius:8, maxHeight:220, overflowY:"auto", boxShadow:"0 8px 20px #0003", marginTop:2 }}>
                  {buildOptions(form._search).map(opt=>(
                    <div key={opt.type+"-"+opt.id} onClick={()=>{
                      if(opt.type==="client"){
                        set("clientId",opt.id); set("clientName",opt.name);
                        set("businessId",""); set("businessName","");
                      } else {
                        set("businessId",opt.id); set("businessName",opt.name);
                        set("clientId",opt.ownerId||""); set("clientName",opt.ownerName||"");
                      }
                      set("_search",null);
                    }}
                      style={{ padding:"8px 12px", cursor:"pointer", borderBottom:"1px solid "+t.border, fontFamily:"'Outfit',sans-serif", display:"flex", alignItems:"center", gap:8 }}
                      onMouseEnter={e=>e.currentTarget.style.background=t.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 6px", borderRadius:5, background: opt.type==="business" ? BRAND.green+"15" : BRAND.blue+"15", color: opt.type==="business" ? BRAND.green : BRAND.blue, flexShrink:0 }}>{opt.type==="business"?"Biz":"Client"}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12.5, fontWeight:600, color:t.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{opt.name}</div>
                        {opt.type==="business" && opt.ownerName && <div style={{ fontSize:11, color:t.textMuted }}>Owner: {opt.ownerName}</div>}
                      </div>
                    </div>
                  ))}
                  {buildOptions(form._search).length===0 && <div style={{ padding:"12px", fontSize:12, color:t.textMuted, textAlign:"center" }}>No matches</div>}
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ padding:"0 24px 20px", display:"flex", gap:8 }}>
          <button onClick={handleCreate} disabled={saving} style={{ ...btnP, flex:1, justifyContent:"center" }}>
            {saving?<><MiniSpinner size={13} color="#fff"/>Creating...</>:"Create Task →"}
          </button>
          <button onClick={onClose} style={btnS(t)}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
